// Lot 684 — « un seul son à la fois » + silence en arrière-plan (garde-fou admin `one_sound`).
// 1) Dès qu'un média SONORE démarre (un <audio> : publication SON, vocal du chat, prière vocale, musique ; ou une <video>
//    non muette : Reel, vidéo du fil avec le son, direct), tout autre média sonore se met en pause — y compris quand on
//    réactive le son d'une vidéo déjà en lecture (événement volumechange). Les vidéos MUETTES du fil ne sont pas touchées.
// 2) App passée en arrière-plan (visibilitychange / pagehide / appStateChange Capacitor) : tout média sonore est mis en
//    pause — la WebView Android ne coupe rien d'elle-même (le son continuait après avoir quitté l'app). Au retour, les
//    vidéos qu'on avait coupées reprennent (elles jouaient d'elles-mêmes) ; un son (<audio>) reste en pause : le membre
//    le relance lui-même, comme sur iPhone.
// Jamais touchés : les flux caméra / appels (srcObject) et les éléments marqués data-keep-background="1".
import { Capacitor } from "@capacitor/core";
import { guardOn } from "@/lib/guards";

const GUARD = "one_sound";
let installed = false;
let pausedByUs = [];

const isMedia = (el) => typeof HTMLMediaElement !== "undefined" && el instanceof HTMLMediaElement;
const isStream = (el) => !!el.srcObject;
const keepsBackground = (el) => el.dataset?.keepBackground === "1";
// Une <video> muette (autoplay du fil) n'est pas un son ; un <audio> l'est toujours.
export const isSounding = (el) => isMedia(el) && !el.paused && !el.ended && !(el.tagName === "VIDEO" && (el.muted || el.volume === 0));

const allMedia = () => Array.from(document.querySelectorAll("audio, video"));

// Met en pause tout autre média sonore que `el` (appelé quand `el` démarre ou retrouve son son).
export const claimSound = (el) => {
  if (!guardOn(GUARD) || !isMedia(el) || isStream(el)) return 0;
  if (el.tagName === "VIDEO" && (el.muted || el.volume === 0)) return 0;
  let n = 0;
  for (const other of allMedia()) {
    if (other === el || isStream(other) || !isSounding(other)) continue;
    try { other.pause(); n += 1; } catch { /* noop */ }
  }
  return n;
};

// Arrière-plan : coupe tout média sonore ; mémorise ceux qu'on a coupés (les deux événements natifs arrivent ensemble →
// la 2e passe ne trouve plus rien à couper et n'efface pas la liste).
export const pauseSoundingMedia = () => {
  if (!guardOn(GUARD)) return 0;
  let n = 0;
  for (const el of allMedia()) {
    if (isStream(el) || keepsBackground(el) || !isSounding(el)) continue;
    try { el.pause(); if (!pausedByUs.includes(el)) pausedByUs.push(el); n += 1; } catch { /* noop */ }
  }
  return n;
};

// Retour au premier plan : seules les VIDÉOS reprennent (elles jouaient d'elles-mêmes) ; les sons restent en pause.
export const resumeAfterBackground = () => {
  const list = pausedByUs;
  pausedByUs = [];
  let n = 0;
  for (const el of list) {
    if (el.tagName !== "VIDEO" || !el.isConnected || el.ended) continue;
    try { const p = el.play(); if (p?.catch) p.catch(() => {}); n += 1; } catch { /* noop */ }
  }
  return n;
};

export const installMediaFocus = () => {
  if (installed || typeof document === "undefined") return;
  installed = true;
  // `play` / `volumechange` ne remontent pas (bubble) : on les capte en phase de capture sur le document.
  document.addEventListener("play", (e) => claimSound(e.target), true);
  document.addEventListener("volumechange", (e) => { if (isSounding(e.target)) claimSound(e.target); }, true);
  document.addEventListener("visibilitychange", () => { if (document.visibilityState === "hidden") pauseSoundingMedia(); else resumeAfterBackground(); });
  window.addEventListener("pagehide", pauseSoundingMedia);
  if (Capacitor.isNativePlatform()) {
    import("@capacitor/app")
      .then(({ App }) => App.addListener("appStateChange", ({ isActive }) => (isActive ? resumeAfterBackground() : pauseSoundingMedia())))
      .catch(() => {});
  }
};

export const _resetMediaFocusForTests = () => { installed = false; pausedByUs = []; };
